import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Section } from './Shared';
type Slide = {
  src: string;
  alt: string;
  title: string;
  caption: string;
};
const slides: Slide[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/pEnk7MaDro5en8D82z827N/calendario_mensual.png',
  alt: 'Calendario Mensual',
  title: 'CALENDARIO MENSUAL',
  caption: 'Planificá tu mes con intención: enfoque, prioridades y notas en una sola vista.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/mp9XDu7gsXU46qvnMkN7Be/Perfil_del_paciente.png',
  alt: 'Perfil del Paciente',
  title: 'PERFIL DEL PACIENTE',
  caption: 'Conocé, comprendé y acompañá. Toda la historia de cada paciente en un solo lugar.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/4GqWHaYuojnhpEu8nqJ7nE/Registro_de_ingresos.png',
  alt: 'Registro de Ingresos',
  title: 'REGISTRO DE INGRESOS',
  caption: 'Conocé y valorá el flujo de ingresos de tu consulta para crecer con claridad.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9xqEyMx3MaX4kpRDmueDvW/diario_imagen1.png',
  alt: 'La Vida que Quiero Construir',
  title: 'LA VIDA QUE QUIERO CONSTRUIR',
  caption: 'Visualizá tu vida ideal como terapeuta y como persona, y alineá tus acciones.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/mKYHEnsKdEpobxrDX6m7W9/diario_imagen_2.png',
  alt: 'Mis Valores como Terapeuta',
  title: 'MIS VALORES COMO TERAPEUTA',
  caption: 'Definí los principios que guían tu práctica y tu vida con propósito.'
}];
export const MaterialPreview = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const goTo = useCallback((next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex((next + slides.length) % slides.length);
  }, [index]);
  const next = useCallback(() => {
    setDirection(1);
    setIndex(i => (i + 1) % slides.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setIndex(i => (i - 1 + slides.length) % slides.length);
  }, []);
  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next, paused]);
  // Touch / swipe support for mobile
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(delta) > 40) {
      if (delta < 0) next();else prev();
    }
    setTouchStartX(null);
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0,
      scale: 0.96
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0,
      scale: 0.96
    })
  };
  return <Section className="max-w-5xl" data-id="element-206">
      <div className="text-center mb-8 md:mb-12" data-id="element-207">
        <div className="inline-flex items-center justify-center bg-white/80 border border-brand-gold/40 px-4 py-1.5 rounded-full mb-5 shadow-sm" data-id="element-208">
          <span className="font-serif font-semibold tracking-wider text-brand-violetDark text-xs md:text-sm" data-id="element-209">
            VISTA PREVIA DEL KIT
          </span>
        </div>
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-display font-bold leading-tight text-brand-violetDark" data-id="element-210">
          📸 Conocé el material por dentro
        </h2>
        <p className="text-brand-text/70 mt-4 text-sm md:text-base max-w-2xl mx-auto" data-id="element-211">
          Planificador principal, bonos y plantillas clínicas — todo diseñado
          para organizar tu consultorio desde hoy mismo.
        </p>
      </div>

      <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-212">
        <div className="relative rounded-3xl overflow-hidden border border-brand-gold/30 bg-gradient-to-br from-brand-violetDark/95 via-brand-violet/90 to-brand-burgundy/90 shadow-[0_20px_60px_rgba(107,68,35,0.25)]" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-id="element-213">
          {/* Decorative texture */}
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none" data-id="element-214"></div>

          <div className="relative w-full flex items-center justify-center px-4 md:px-16 py-8 md:py-12" style={{
          minHeight: '420px'
        }} data-id="element-215">
            <AnimatePresence custom={direction} mode="wait" data-id="element-216">
              <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 260,
                damping: 30
              },
              opacity: {
                duration: 0.3
              },
              scale: {
                duration: 0.3
              }
            }} className="flex flex-col items-center justify-center w-full" data-id="element-217">
                <div className="relative" data-id="element-218">
                  {/* Soft glow behind image */}
                  <div className="absolute -inset-4 bg-white/10 blur-2xl rounded-3xl" data-id="element-219"></div>
                  <img src={slides[index].src} alt={slides[index].alt} draggable={false} className="relative max-h-[320px] md:max-h-[420px] w-auto max-w-full drop-shadow-2xl select-none" data-id="element-220" />
                </div>

                <div className="relative mt-5 md:mt-6 text-center max-w-md" data-id="element-221">
                  <p className="font-display font-bold text-white text-sm md:text-lg tracking-wide drop-shadow" data-id="element-222">
                    {slides[index].title}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <button type="button" onClick={prev} aria-label="Anterior" className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-gold/40 shadow-md flex items-center justify-center text-brand-violet hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-223">
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-224" />
          </button>
          <button type="button" onClick={next} aria-label="Siguiente" className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-gold/40 shadow-md flex items-center justify-center text-brand-violet hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-225">
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-226" />
          </button>
        </div>

        {/* Caption below carousel */}
        <div className="mt-5 text-center px-4 min-h-[2.5rem]" data-id="element-227">
          <AnimatePresence mode="wait" data-id="element-228">
            <motion.p key={index} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.25
          }} className="text-sm md:text-base font-medium text-brand-violetDark" data-id="element-229">
              {slides[index].caption}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-2 mt-4" data-id="element-230">
          {slides.map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Ir a la imagen ${i + 1}`} className={`h-2 rounded-full transition-all ${i === index ? 'w-7 bg-brand-violet' : 'w-2 bg-brand-violet/20 hover:bg-brand-violet/50'}`} data-id="element-231" />)}
        </div>
        <p className="text-center text-xs text-brand-muted mt-3" data-id="element-232">
          {index + 1} / {slides.length} · Deslizá o usá las flechas
        </p>
      </div>
    </Section>;
};